import * as signalR from '@microsoft/signalr';
import { TruckDto, UrlKeyDto } from '@trucktrax/trucktrax-ts-common';
import recordLog from '../../util/errorUtil';
import { acknowledgeMessage, updateMessages } from '../../store/actions/messageActions';
import { evictTruckFromCache, updateTruckAssignment } from '../../store/actions/truckActions';
import { store } from '../../Main';
import { getInboxMessages } from '../messagesService';
import { setReduxAllTruckList } from '../trucksService';
import { JOIN_GROUP, RECEIVE_MESSAGE } from '../../constants/appConstants';
import {
  ACKNOWLEDGE_MESSAGE_GROUP,
  NEW_MESSAGE_GROUP,
  MESSAGING_SOCKET,
  TRUCK_ASSIGNMENT_GROUP,
} from '../../constants/apiConstants';
import { getBridgeBaseUrl } from '../../util/apiUtil';
import { getTokens, isExpired } from '../../util/authUtil';
import { ensureFreshAccessToken, RefreshOutcome } from '../requestService';
import { ConnectionState, isAuthFailureError, ReconnectScheduler } from '../../util/socketUtil';
import { allPromisesResolved } from '../../util/appUtil';

type Subscription = { url?: string; unsubscribe: () => void };

/**
 * The change an incoming truck-assignment event makes to the cached truck list
 * (DEV-3840). A plain action, so a mutation held back during a refetch replays
 * by being dispatched again -- no separate encoding to decode.
 */
type TruckCacheMutation =
  | ReturnType<typeof updateTruckAssignment>
  | ReturnType<typeof evictTruckFromCache>;

/**
 * What an incoming truck-assignment event does to the cached list, or
 * `undefined` when it is a no-op for this dispatcher (DEV-3840).
 *
 * The payload is the truck's post-change state, so both directions are the same
 * event: `driver` populated on assign, absent on unassign.
 *
 * `allTrucks` is fetched region + primaryProductLine scoped, so anything outside
 * the dispatcher's current view is evicted rather than folded in -- an
 * out-of-scope truck would otherwise surface in the recipient picker. The region
 * check is belt-and-braces (the group is already region-scoped) and costs
 * nothing.
 *
 * Deciding here rather than in the caller keeps the scope rules free of the
 * store, so applying a mutation and buffering it for replay are the same value.
 */
function truckCacheMutationFor(
  truck: TruckDto | undefined,
  currentRegion?: UrlKeyDto,
  selectedProductLine?: string,
): TruckCacheMutation | undefined {
  if (!truck?.url) {
    return undefined;
  }

  const inScope =
    truck.region?.url === currentRegion?.url && truck.primaryProductLine === selectedProductLine;
  if (inScope) {
    return updateTruckAssignment(truck);
  }

  // The truck left the dispatcher's view -- an admin moved its region or flipped
  // its product line. Evict rather than ignore: the cached record still carries
  // a driver pairing, and buildTruckMap would keep labelling that driver with a
  // truck they are no longer reachable on.
  //
  // Only act on a scope decision the payload actually carries. A partial payload
  // missing either field reads as out-of-scope here, and evicting on that would
  // silently pull a valid truck out of the recipient picker -- a worse failure
  // than leaving it stale until the next refetch.
  const payloadCarriesScope = !!truck.region?.url && !!truck.primaryProductLine;
  return payloadCarriesScope ? evictTruckFromCache(truck) : undefined;
}

export class MessagingSocket {
  connectionState: ConnectionState;

  hubConnection: signalR.HubConnection | null;

  private regionUrl: string | null;

  private shouldReconnectOnClose: boolean = true;

  selectedProductLine?: string;

  /**
   * The dispatcher id of the last successful (or attempted) connection. Stored
   * so {@link reconnect} can re-invoke {@link connect} without callers having
   * to re-supply arguments.
   */
  private dispatcherId?: string;

  /**
   * The subscription list passed to the last {@link connect}. Preserved across
   * reconnects so subscription lifecycle hooks remain wired up.
   */
  private currentSubscriptions?: Subscription[];

  /**
   * Owns backoff state (attempt counter + pending timer) for this socket.
   */
  private reconnectScheduler = new ReconnectScheduler();

  /**
   * True while recovering from a dropped connection, so a successful connect
   * knows to refetch the truck list (DEV-3840). Stays set through failed
   * attempts and is cleared only once the refetch has been kicked off.
   */
  private recoveringFromDrop = false;

  /**
   * Bookkeeping for the post-reconnect truck-list refetch (DEV-3840). Instance
   * state alongside {@link recoveringFromDrop} so a rebuilt socket starts clean
   * rather than inheriting a half-finished refetch's flag or buffer.
   */
  private truckRefetchInFlight = false;

  private bufferedTruckCacheMutations: TruckCacheMutation[] = [];

  constructor() {
    this.connectionState = ConnectionState.Disconnected;
    this.hubConnection = null;
    this.regionUrl = null;
  }

  onResponse = (response: string, regionUrl?: string) => {
    const { message, group } = JSON.parse(response);
    const { selectedProductLine, currentRegion } = store.getState();
    if (group === ACKNOWLEDGE_MESSAGE_GROUP) {
      store.dispatch(acknowledgeMessage(message));
    } else if (group === NEW_MESSAGE_GROUP) {
      if (message.productLine !== selectedProductLine) {
        return;
      }
      store.dispatch(updateMessages(message, regionUrl ?? ''));
    } else if (group === TRUCK_ASSIGNMENT_GROUP) {
      this.onTruckAssignment(message, currentRegion, selectedProductLine);
    }
  };

  /**
   * A driver was assigned to / unassigned from a truck, or a truck was moved out
   * of the dispatcher's scope (DEV-3840).
   */
  private onTruckAssignment(
    truck: TruckDto | undefined,
    currentRegion?: UrlKeyDto,
    selectedProductLine?: string,
  ) {
    const mutation = truckCacheMutationFor(truck, currentRegion, selectedProductLine);
    if (!mutation) {
      return;
    }

    store.dispatch(mutation);

    // A refetch in flight is about to replace the whole array with a snapshot
    // taken before this mutation. Remember it so refreshTruckList re-applies it.
    if (this.truckRefetchInFlight) {
      this.bufferedTruckCacheMutations.push(mutation);
    }
  }

  /**
   * Refetches the cached truck list after recovering from a dropped connection
   * (DEV-3840).
   *
   * SignalR groups do not replay, so assignment events raised while the socket
   * was down are gone -- and some unassignments never emit at all, because the
   * auth-backend forced-logout path clears the driver through
   * trucks/logout-trucks and bypasses the geotrax assignment flow entirely.
   *
   * FETCH_ALL_TRUCKS replaces the array wholesale, so any assignment that lands
   * while this GET is in flight would be reverted by a snapshot the server took
   * before the change. Those patches are buffered and re-applied on top.
   */
  private async refreshTruckList(regionUrl: string, productLine?: string) {
    this.truckRefetchInFlight = true;
    this.bufferedTruckCacheMutations = [];

    try {
      await store.dispatch<any>(setReduxAllTruckList(regionUrl, productLine));
    } finally {
      this.truckRefetchInFlight = false;
    }

    const replay = this.bufferedTruckCacheMutations;
    this.bufferedTruckCacheMutations = [];
    replay.forEach(mutation => store.dispatch(mutation));
  }

  async connect(
    dispatcherId: string,
    currentRegion?: UrlKeyDto,
    currentSubscriptions?: Subscription[],
  ) {
    const { tenantId: tenant, selectedProductLine } = store.getState();

    let { accessToken } = getTokens();
    // do not try to connect if we're not logged in
    if (!accessToken) {
      return;
    }

    // Remember the args before any early return so a scheduled reconnect can
    // recover even when this first attempt bails on an expired token.
    this.dispatcherId = dispatcherId;
    this.currentSubscriptions = currentSubscriptions;

    // An expired token 403s on /negotiate and spins the reconnect loop, so
    // refresh first. transient -> keep backing off; invalid -> user logged out.
    if (isExpired(accessToken)) {
      const refreshOutcome = await ensureFreshAccessToken();
      switch (refreshOutcome) {
        case RefreshOutcome.Refreshed:
          break;
        case RefreshOutcome.Transient:
          this.reconnect();
          return;
        case RefreshOutcome.Invalid:
          return;
        default:
          return;
      }
      accessToken = getTokens().accessToken;
      if (!accessToken) {
        return;
      }
    }

    if (!currentRegion?.url) {
      return;
    }

    const regionUrl = currentRegion.url.trim();
    const isRedundantConnectionAttempt =
      this.regionUrl === regionUrl &&
      (this.connectionState === ConnectionState.Connecting ||
        this.connectionState === ConnectionState.Connected);
    if (isRedundantConnectionAttempt) {
      return;
    }

    this.connectionState = ConnectionState.Connecting;
    this.regionUrl = regionUrl;

    const MESSAGING_SOCKET_URL = `${getBridgeBaseUrl()}${MESSAGING_SOCKET}`;

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${MESSAGING_SOCKET_URL}?tk=${accessToken}`, {
        accessTokenFactory: () => accessToken!,
      })
      .configureLogging(signalR.LogLevel.None)
      .build();

    this.hubConnection.serverTimeoutInMilliseconds = 30_000;
    this.hubConnection.on(RECEIVE_MESSAGE, (message: string) => {
      this.onResponse(message, regionUrl);
    });
    const localHubConnection = this.hubConnection;
    this.shouldReconnectOnClose = true;

    this.hubConnection.onclose(_ => {
      // Mark the connection as down and route through reconnect() so the
      // exponential backoff applies. Directly calling connect() here (the prior
      // behavior) produced a tight loop when the server accepted then quickly
      // dropped the socket, because start() kept succeeding.
      const matchingConnection = this.hubConnection === localHubConnection;
      if (!matchingConnection) {
        return;
      }

      this.clearHubConnectionState(true);
      if (this.shouldReconnectOnClose) {
        this.reconnect();
      }
    });

    try {
      await localHubConnection.start();
      // TODO: Remove tenant from websocket group (work is done in future card)
      const groupJoinResponses = await Promise.allSettled(
        [ACKNOWLEDGE_MESSAGE_GROUP, NEW_MESSAGE_GROUP, TRUCK_ASSIGNMENT_GROUP]
          .map(group => `${group}${tenant}/${regionUrl}`)
          .map(group => localHubConnection.invoke(JOIN_GROUP, group)),
      );

      const allGroupsConnected = allPromisesResolved(groupJoinResponses);
      if (!allGroupsConnected) {
        throw new Error('Failed to establish all group connections forcing reconnect');
      }
      const matchingConnection = this.hubConnection === localHubConnection;
      if (matchingConnection) {
        this.connectionState = ConnectionState.Connected;
        // Full handshake + group joins succeeded, so reset backoff. A later drop
        // should recover promptly rather than inherit stale delay.
        this.reconnectScheduler.reset();
        // retrieving all messages after connection to account for new messages received
        // from app refresh to websocket connection success
        store.dispatch<any>(
          getInboxMessages(undefined, undefined, currentRegion, selectedProductLine),
        );
        // Only after a drop. An app-driven connect (initial load, region change)
        // is already accompanied by App's own updateAllTrucks(), so refetching
        // here as well would just issue the same GET twice.
        if (this.recoveringFromDrop) {
          this.recoveringFromDrop = false;
          void this.refreshTruckList(regionUrl, selectedProductLine);
        }
      }
    } catch (err) {
      recordLog('warn', 'Messaging Socket', err);
      const matchingConnection = this.hubConnection === localHubConnection;
      if (matchingConnection) {
        this.clearHubConnectionState(true);
        // A 401/403 here is the server refusing a token the client thinks is
        // valid (e.g. auth outage rejecting good tokens, DEV-4179): refresh,
        // then retry -- unless the session is definitively gone.
        if (isAuthFailureError(err)) {
          const refreshOutcome = await ensureFreshAccessToken();
          if (refreshOutcome === RefreshOutcome.Invalid) {
            return;
          }
        }
        this.reconnect();
      }
    }
  }

  /**
   * Queues the next reconnect attempt using capped exponential backoff.
   * Re-reads the currently selected region from the Redux store at fire time
   * so that a region change during the outage reconnects to the new region
   * rather than the stale one captured when the socket was first opened.
   */
  reconnect() {
    // Never reconnect over a live/opening socket -- a connect() that lands
    // Connected during the 403-catch refresh await must not be orphaned (PR #1217).
    if (
      this.connectionState === ConnectionState.Connecting ||
      this.connectionState === ConnectionState.Connected
    ) {
      return;
    }

    if (
      this.connectionState === ConnectionState.Reconnecting &&
      this.reconnectScheduler.isPending
    ) {
      return;
    }

    this.connectionState = ConnectionState.Reconnecting;
    this.reconnectScheduler.schedule(async () => {
      if (!this.dispatcherId) {
        return;
      }
      // Whatever happens to this attempt, the next successful connect is a
      // recovery and owes the truck list a refetch.
      this.recoveringFromDrop = true;
      const { currentRegion: region } = store.getState();
      if (this.reconnectScheduler.currentAttempt >= 2) {
        // Rebuild the (possibly wedged) hub but preserve backoff -- resetting
        // it here pins persistent failures at a 2-4s retry storm (DEV-4179).
        await this.disconnect(true, { preserveBackoff: true });
        await this.connect(this.dispatcherId, region, this.currentSubscriptions);
        return;
      }
      await this.connect(this.dispatcherId, region, this.currentSubscriptions);
    });
  }

  async disconnect(destroyHub?: boolean, { preserveBackoff = false } = {}): Promise<void> {
    this.shouldReconnectOnClose = false;
    if (!preserveBackoff) {
      this.reconnectScheduler.reset();
      // A disconnect that also resets backoff is a fresh app-driven action
      // (e.g. a region change), not a continuation of an in-progress drop
      // recovery. Without this, a failed reconnect attempt leaves the flag
      // armed, and the next connect it sees -- even an unrelated one -- fires
      // the recovery refetch it was never meant to own.
      this.recoveringFromDrop = false;
    }

    try {
      if (this.hubConnection) {
        await this.hubConnection.stop();
      }
    } catch (_: unknown) {
      // Suppress errors from stop()
    } finally {
      this.clearHubConnectionState(destroyHub);
      this.shouldReconnectOnClose = true;
    }
  }

  private clearHubConnectionState(destroyHub: boolean = false): void {
    if (this.connectionState !== ConnectionState.Disconnected) {
      this.connectionState = ConnectionState.Disconnected;
    }

    if (destroyHub && this.hubConnection) {
      this.hubConnection.off(RECEIVE_MESSAGE);
      this.hubConnection = null;
    }

    this.regionUrl = null;
  }

  public async attemptConnectionChange(
    region?: UrlKeyDto,
    currentSubscriptions?: Subscription[],
  ): Promise<void> {
    if (!region?.url) {
      return;
    }

    const regionUrl = region.url.trim();
    if (this.regionUrl === regionUrl && this.connectionState === ConnectionState.Connected) {
      return;
    }

    const { userUrl } = store.getState();
    const dispatcherId = this.dispatcherId ?? userUrl ?? '';
    const subscriptions = currentSubscriptions ?? this.currentSubscriptions;

    await this.disconnect(true);
    await this.connect(dispatcherId, region, subscriptions);
  }
}

export default new MessagingSocket();
